import { Link } from "@inertiajs/react"
import * as Dialog from "@radix-ui/react-dialog"
import { Command } from "cmdk"
import { Building2, Check, ChevronDown, Eye, EyeOff, Plus, Search, Star, Wallet, X } from "lucide-react"
import { useEffect, useState, type CSSProperties, type RefObject } from "react"
import { CompanyLogo } from "@/components/people/company-logo"
import { CountPill } from "@/components/people/count-pill"
import { RoleBadge } from "@/components/people/role-badge"
import { Button } from "@/components/ui/primitives"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { useStoredIds } from "@/hooks/use-stored-ids"
import { useTranslation } from "@/hooks/use-translation"
import { formatRubWhole } from "@/lib/billing-format"
import { displayAppName } from "@/lib/brand"
import { groupCompaniesByLetter } from "@/lib/company-switcher"
import { getJson } from "@/lib/json-request"
import { filterBySubstring } from "@/lib/substring-filter"
import { respondToInvitation, switchCompany } from "@/lib/switch-company"
import { cn } from "@/lib/utils"
import { useTaskNotificationsStore } from "@/stores/task-notifications-store"
import type { AuthCompanyOption, AuthShare, IncomingInvitation } from "@/types/auth"

const RECENT_LIMIT = 6
const EMPTY_COUNTS: Record<string, number> = {}

const ROW_CLASS =
  "flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg pr-1.5 pl-2 text-left text-sm text-ui-text-secondary"
const ROW_CURRENT_CLASS = "bg-[var(--ui-nav-active-bg)] text-[var(--ui-nav-active-fg)]"
const ROW_IDLE_CLASS = "data-[selected=true]:bg-ui-surface-raised data-[selected=true]:text-ui-text-primary"
const SECTION_LABEL_CLASS = "px-1 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase"
const GROUP_CLASS =
  "min-w-0 break-inside-avoid pb-5 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[13px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-ui-text-primary [&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-col [&_[cmdk-group-items]]:gap-0.5"
// Скрытые — одна группа без букв, поэтому сеткой: колонки CSS не разрезали бы её.
const HIDDEN_GROUP_CLASS =
  "[&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:gap-x-6 [&_[cmdk-group-items]]:gap-y-0.5 @md:[&_[cmdk-group-items]]:grid-cols-2 @2xl:[&_[cmdk-group-items]]:grid-cols-3 [&_[cmdk-item]]:text-ui-text-muted"
const ACTION_CLASS =
  "size-6 shrink-0 items-center justify-center rounded-md text-ui-text-faint hover:bg-ui-surface-hover hover:text-ui-text-primary"
// Не opacity, а display: невидимые кнопки съедали бы ширину названия в узкой колонке.
const ACTION_HOVER_ONLY = "hidden group-hover:flex group-data-[selected=true]:flex"

type Balances = Record<string, number>

// Ищем только по названию: служебное value «company:N» само совпадало бы с «a» или цифрами.
const filterByName = (_value: string, search: string, keywords: string[] = []) =>
  filterBySubstring("", search, keywords)

export type CompanyMenuProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Куда вернуть фокус после закрытия: панель открывается не через Dialog.Trigger. */
  returnFocusRef: RefObject<HTMLElement | null>
  auth: AuthShare
  onAction: () => void
}

/**
 * Панель смены компании справа от сайдбара, в формате «Администрирования»: компании
 * по алфавиту в колонках, поиск, избранное, недавние и скрытые. Списки — в localStorage.
 */
export function CompanyMenu({ open, onOpenChange, returnFocusRef, auth, onAction }: CompanyMenuProps) {
  const { t } = useTranslation("layout")
  const { t: notificationsT } = useTranslation("notifications")
  const counts = useTaskNotificationsStore((s) => s.summary?.companies ?? EMPTY_COUNTS)
  const storagePrefix = `it2tv:companyMenu:${auth.user?.id ?? "anon"}`
  const [favorites, setFavorites] = useStoredIds(`${storagePrefix}:favorites`)
  const [recent, setRecent] = useStoredIds(`${storagePrefix}:recent`)
  const [hidden, setHidden] = useStoredIds(`${storagePrefix}:hidden`)
  const [search, setSearch] = useState("")
  const [tab, setTab] = useState("favorites")
  const [showHidden, setShowHidden] = useState(false)
  const [balances, setBalances] = useState<Balances | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const currentId = auth.company?.id ?? null
  const invitations = auth.incoming_invitations
  const searching = search.trim() !== ""

  // Остатки свежие на каждое открытие; до ответа строки без суммы, ошибка — тоже без суммы.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    getJson<{ balances: Balances }>("/company_switcher/balances")
      .then((data) => {
        if (!cancelled) setBalances(data.balances)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [open])

  const openChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      setSearch("")
      setShowHidden(false)
    }
  }

  const byId = new Map(auth.companies.map((company) => [String(company.id), company]))
  const nameOf = (company: AuthCompanyOption) => displayAppName(company)
  // Пока ищут, скрытые тоже находятся: скрыть — убрать из списка, а не потерять доступ.
  const listed = searching ? auth.companies : auth.companies.filter((company) => !hidden.includes(String(company.id)))
  const groups = groupCompaniesByLetter(listed, nameOf)
  const hiddenCompanies = auth.companies.filter((company) => hidden.includes(String(company.id)))
  const quickIds = tab === "favorites" ? favorites : recent
  const quickCompanies = quickIds.flatMap((id) => byId.get(id) ?? [])
  // Две строки только когда суммы вообще есть: у участника без прав на биллинг строки остаются низкими.
  const twoLine = balances != null && Object.keys(balances).length > 0
  const anchorStyle = { "--company-menu-left": "var(--shell-sidebar-width)" } as CSSProperties

  const toggle = (ids: string[], id: string) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])

  const selectCompany = (company: AuthCompanyOption) => {
    openChange(false)
    const id = String(company.id)
    setRecent([id, ...recent.filter((x) => x !== id)].slice(0, RECENT_LIMIT))
    if (company.id === currentId) return
    onAction()
    switchCompany(company.id)
  }

  const respond = (invitation: IncomingInvitation, action: "accept" | "decline") => {
    if (busyId != null) return
    respondToInvitation(invitation.id, action, {
      onStart: () => setBusyId(invitation.id),
      onFinish: () => {
        setBusyId(null)
        openChange(false)
        onAction()
      },
    })
  }

  const companyBody = (company: AuthCompanyOption) => {
    const name = nameOf(company)
    const balance = balances?.[String(company.id)]
    const count = counts[String(company.id)] ?? 0
    return (
      <>
        <CompanyLogo company={{ ...company, name }} size={24} />
        {/* Колонка не уже суммы: название обрезается, сумма не переносится. */}
        <span className="flex min-w-min flex-1 flex-col">
          <span className="w-0 min-w-full truncate leading-[18px]" title={name}>
            {name}
          </span>
          {balance != null ? (
            <span
              className={cn(
                "flex items-center gap-1 text-[11px] leading-4 whitespace-nowrap tabular-nums",
                company.id === currentId ? "opacity-75" : "text-ui-text-muted",
                balance < 0 && "text-ui-status-failed opacity-100 light:text-ui-danger",
              )}
            >
              <Wallet className="h-3 w-3 shrink-0" />
              {formatRubWhole(balance)}
            </span>
          ) : null}
        </span>
        {count > 0 ? (
          <span className="shrink-0" title={notificationsT("companyUnread", { count })}>
            <CountPill>{count > 99 ? "99+" : count}</CountPill>
          </span>
        ) : null}
        {company.role ? <RoleBadge scope="company" role={company.role} /> : null}
      </>
    )
  }

  const companyItem = (company: AuthCompanyOption) => {
    const id = String(company.id)
    const favorite = favorites.includes(id)
    const isHidden = hidden.includes(id)
    const current = company.id === currentId
    return (
      <Command.Item
        key={id}
        value={`company:${id}`}
        keywords={[nameOf(company)]}
        onSelect={() => selectCompany(company)}
        className={cn(ROW_CLASS, "group", twoLine ? "h-11" : "h-9", current ? ROW_CURRENT_CLASS : ROW_IDLE_CLASS)}
      >
        {companyBody(company)}
        {/* Звезда и глаз — для мыши: Enter в cmdk переключает компанию. */}
        <button
          type="button"
          tabIndex={-1}
          aria-pressed={favorite}
          aria-label={favorite ? t("shell.companyMenu.removeFavorite") : t("shell.companyMenu.addFavorite")}
          title={favorite ? t("shell.companyMenu.removeFavorite") : t("shell.companyMenu.addFavorite")}
          onClick={(event) => {
            event.stopPropagation()
            setFavorites(toggle(favorites, id))
          }}
          className={cn(ACTION_CLASS, favorite ? "flex text-ui-status-review" : ACTION_HOVER_ONLY)}
        >
          <Star className={cn("h-3.5 w-3.5", favorite && "fill-amber-400")} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label={isHidden ? t("shell.companyMenu.unhide") : t("shell.companyMenu.hide")}
          title={isHidden ? t("shell.companyMenu.unhide") : t("shell.companyMenu.hide")}
          onClick={(event) => {
            event.stopPropagation()
            setHidden(toggle(hidden, id))
          }}
          className={cn(ACTION_CLASS, ACTION_HOVER_ONLY)}
        >
          {isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        </button>
        {current ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
      </Command.Item>
    )
  }

  // Строка боковой колонки: избранное и недавние, без действий.
  const quickRow = (company: AuthCompanyOption) => (
    <button
      key={company.id}
      type="button"
      onClick={() => selectCompany(company)}
      className={cn(
        ROW_CLASS,
        twoLine ? "h-11" : "h-9",
        company.id === currentId ? ROW_CURRENT_CLASS : "hover:bg-ui-surface-raised hover:text-ui-text-primary",
      )}
    >
      {companyBody(company)}
    </button>
  )

  return (
    <Dialog.Root open={open} onOpenChange={openChange}>
      <Dialog.Portal>
        {/* Ниже хедера и справа от сайдбара: только основной блок. */}
        <Dialog.Overlay
          style={anchorStyle}
          className="ui-dialog-overlay fixed inset-0 z-[150] bg-[var(--ui-overlay)] md:top-[var(--shell-window-bar-height)] md:left-[var(--company-menu-left)]"
        />
        <Dialog.Content
          style={anchorStyle}
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            returnFocusRef.current?.focus()
          }}
          className={cn(
            "ui-dialog-fade fixed inset-0 z-[150] flex flex-col overflow-hidden bg-ui-bg-elevated text-ui-text-primary focus:outline-none",
            "md:top-[calc(var(--shell-window-bar-height)+0.5rem)] md:right-auto md:bottom-2 md:left-[calc(var(--company-menu-left)+0.5rem)]",
            "md:w-[min(65rem,calc(100vw-var(--company-menu-left)-1rem))]",
            "md:rounded-lg md:border md:border-ui-border md:shadow-2xl md:shadow-black/40",
          )}
        >
          <Dialog.Title className="sr-only">{t("shell.companyMenu.open")}</Dialog.Title>
          <Command loop filter={filterByName} className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-ui-border pr-2 pl-4">
              <Search className="h-4 w-4 shrink-0 text-ui-text-muted" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={t("shell.companyMenu.search")}
                className="h-10 min-w-0 flex-1 border-0 bg-transparent text-[15px] text-ui-text-primary outline-none placeholder:text-ui-text-faint focus:ring-0"
              />
              <Dialog.Close
                type="button"
                className="rounded-lg p-2 text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("shell.companyMenu.close")}
              >
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>

            <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[14.5rem_minmax(0,1fr)] md:grid-rows-1">
              <aside
                className={cn(
                  "flex flex-col gap-5 border-b border-ui-border p-3 md:overflow-y-auto md:border-r md:border-b-0",
                  searching && "max-md:hidden",
                )}
              >
                <div className="flex flex-col gap-2">
                  <SegmentedControl
                    value={tab}
                    onChange={setTab}
                    className="self-start"
                    items={[
                      { id: "favorites", label: t("shell.companyMenu.favorites") },
                      { id: "recent", label: t("shell.companyMenu.recent") },
                    ]}
                  />
                  {quickCompanies.length > 0 ? (
                    <div className="flex flex-col gap-0.5">{quickCompanies.map(quickRow)}</div>
                  ) : (
                    <p className="px-1 text-xs leading-relaxed text-ui-text-muted">
                      {tab === "favorites" ? t("shell.companyMenu.favoritesEmpty") : t("shell.companyMenu.recentEmpty")}
                    </p>
                  )}
                </div>

                {invitations.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className={SECTION_LABEL_CLASS}>
                      {t("shell.companyMenu.invitations", { count: invitations.length })}
                    </div>
                    {invitations.map((invitation) => {
                      const busy = busyId === invitation.id
                      return (
                        <div key={invitation.id} className="flex flex-col gap-1.5 px-1">
                          <div className="flex min-w-0 items-center gap-2 text-xs text-ui-text-primary">
                            <CompanyLogo
                              company={{ id: invitation.company_id, name: invitation.company_name }}
                              size={24}
                              pending
                            />
                            <span className="min-w-0 flex-1 truncate" title={invitation.company_name}>
                              {invitation.company_name}
                            </span>
                            <RoleBadge scope="company" role={invitation.company_role} />
                          </div>
                          <div className="flex gap-1.5 pl-8">
                            <Button
                              type="button"
                              size="sm"
                              className="h-6 px-2.5 text-[11px] shadow-none"
                              disabled={busy}
                              aria-label={`${t("shell.companyMenu.accept")} · ${invitation.company_name}`}
                              onClick={() => respond(invitation, "accept")}
                            >
                              {t("shell.companyMenu.accept")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2.5 text-[11px]"
                              disabled={busy}
                              aria-label={`${t("shell.companyMenu.decline")} · ${invitation.company_name}`}
                              onClick={() => respond(invitation, "decline")}
                            >
                              {t("shell.companyMenu.decline")}
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}

                <div className="mt-auto flex flex-col gap-0.5 border-t border-ui-border pt-3 max-md:hidden">
                  <Link
                    href="/companies/new"
                    onClick={() => openChange(false)}
                    className={cn(ROW_CLASS, "h-9 hover:bg-ui-surface-raised hover:text-ui-text-primary")}
                  >
                    <Plus className="h-4 w-4 shrink-0 opacity-70" />
                    {t("shell.companyMenu.createCompany")}
                  </Link>
                  {auth.company ? (
                    <Link
                      href="/company/overview"
                      onClick={() => openChange(false)}
                      className={cn(ROW_CLASS, "h-9 hover:bg-ui-surface-raised hover:text-ui-text-primary")}
                    >
                      <Building2 className="h-4 w-4 shrink-0 opacity-70" />
                      {t("shell.companyMenu.about")}
                    </Link>
                  ) : null}
                </div>
              </aside>

              <Command.List className="@container min-h-0 overflow-y-auto px-3 py-4 md:px-5 md:py-5">
                <Command.Empty className="px-2.5 py-10 text-sm text-ui-text-muted">
                  {t("shell.companyMenu.searchEmpty")}
                </Command.Empty>
                {/* Колонки по ширине панели; буквы идут сверху вниз, как и стрелки cmdk. */}
                <div className="gap-x-6 @md:columns-2 @2xl:columns-3">
                  {groups.map((group) => (
                    <Command.Group key={group.letter} heading={group.letter} className={GROUP_CLASS}>
                      {group.items.map(companyItem)}
                    </Command.Group>
                  ))}
                </div>

                {!searching && hiddenCompanies.length > 0 ? (
                  <div className="mt-1 border-t border-ui-border pt-3">
                    <button
                      type="button"
                      aria-expanded={showHidden}
                      onClick={() => setShowHidden((value) => !value)}
                      className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                    >
                      <EyeOff className="h-3.5 w-3.5" />
                      {t("shell.companyMenu.hidden", { count: hiddenCompanies.length })}
                      <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showHidden && "rotate-180")} />
                    </button>
                    {showHidden ? (
                      <Command.Group className={cn("mt-3", HIDDEN_GROUP_CLASS)}>
                        {groupCompaniesByLetter(hiddenCompanies, nameOf).flatMap((group) =>
                          group.items.map(companyItem),
                        )}
                      </Command.Group>
                    ) : null}
                  </div>
                ) : null}
              </Command.List>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
